import { useCallback } from 'react';
import { CatchNotFound, createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { NotFoundPage } from '@features/auth/pages/NotFoundPage';
import { SiteLayoutWidget } from '@features/shared/ui/SiteLayout/SiteLayoutWidget';
import {
  TournamentEditPage,
  TournamentEditPageSkeleton,
} from '@features/tournament/pages/TournamentEditPage';
import { useTournamentEdit } from '@features/tournament/pages/TournamentEditPage/useTournamentEdit';
import { TournamentParticipantsEditWidget } from '@features/tournament/ui/components/TournamentParticipantsEdit';

const querySchema = z.object({
  pageTab: z.enum(['settings', 'participants']).catch('settings').optional(),
  noCopy: z.boolean().optional(),
  participantsTab: z
    .enum(['Invited', 'Participating', 'Exited', 'Banned', 'JoinRequest', 'Removed', 'Deleted'])
    .catch('Participating')
    .optional(),
});
type Query = z.infer<typeof querySchema>;

export const Route = createFileRoute('/club/$clubId_/tournament/$tournamentId_/edit')({
  component: () => (
    <CatchNotFound fallback={() => <NotFoundPage subTitle="Турнир не найден" />}>
      <Page />
    </CatchNotFound>
  ),
  validateSearch: querySchema,
});

function Page() {
  const { clubId, tournamentId } = Route.useParams();
  const {
    pageTab = 'settings',
    participantsTab = 'Participating',
    noCopy = false,
  } = Route.useSearch();

  const navigate = useNavigate({ from: Route.fullPath });

  const handlePageTabChange = useCallback(
    (newTab: Query['pageTab']) => {
      const newParticipantsTab = newTab === 'settings' ? undefined : 'Participating';
      navigate({
        search: (prev) => ({
          ...prev,
          pageTab: newTab,
          participantsTab: newParticipantsTab,
        }),
        ignoreBlocker: true,
      });
    },
    [navigate],
  );
  const handleParticipantsTabChange = useCallback(
    (newTab: Query['participantsTab']) =>
      navigate({
        search: (prev) => ({ ...prev, participantsTab: newTab }),
        ignoreBlocker: true,
      }),
    [navigate],
  );

  const props = useTournamentEdit({
    clubId,
    tournamentId,
    pageTab,
    onPageTabChange: handlePageTabChange,
    noCopy,
  });

  const { isManager } = useClubRoles(clubId);
  if (!isManager) {
    return (
      <Navigate to={`/club/$clubId/tournament/$tournamentId`} params={{ clubId, tournamentId }} />
    );
  }

  return (
    <SiteLayoutWidget clubId={clubId} hideCreateTournament>
      {!props && <TournamentEditPageSkeleton />}
      {props && (
        <TournamentEditPage
          {...props}
          enableUnsavedWarning
          participants={
            <TournamentParticipantsEditWidget
              tournament={props.tournament}
              tab={participantsTab}
              onTab={handleParticipantsTabChange}
            />
          }
        />
      )}
    </SiteLayoutWidget>
  );
}
